import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  Info, 
  MessageSquare,
  Calendar,
  FileText,
  Users,
  X,
  Filter,
  Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NotificationCenter = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'referral',
      title: 'New Referral Received',
      message: 'Dr. Sarah Johnson sent a referral for wisdom teeth extraction',
      priority: 'high',
      read: false,
      timestamp: '5 minutes ago',
      icon: <FileText className="h-5 w-5" />
    },
    {
      id: 2,
      type: 'appointment',
      title: 'Appointment Reminder',
      message: 'Patient John Doe has an appointment tomorrow at 10 AM',
      priority: 'medium',
      read: false,
      timestamp: '2 hours ago',
      icon: <Calendar className="h-5 w-5" />
    },
    {
      id: 3,
      type: 'message',
      title: 'New Message',
      message: 'Dr. Michael Chen replied to your message in Deal Room #42',
      priority: 'medium',
      read: true,
      timestamp: '4 hours ago',
      icon: <MessageSquare className="h-5 w-5" />
    },
    {
      id: 4,
      type: 'system',
      title: 'System Update',
      message: 'New analytics features are now available on your dashboard',
      priority: 'low',
      read: true,
      timestamp: '1 day ago',
      icon: <Info className="h-5 w-5" />
    },
    {
      id: 5,
      type: 'challenge',
      title: 'Challenge Milestone Reached',
      message: 'Congratulations! You completed 25 referrals in the November Sprint',
      priority: 'medium',
      read: false,
      timestamp: '2 days ago',
      icon: <Award className="h-5 w-5" />
    }
  ]);

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const deleteAll = () => {
    if (window.confirm('Delete all notifications?')) {
      setNotifications([]);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getTypeColor = (type) => {
    const colors = {
      referral: 'text-[#D4AF37]',
      appointment: 'text-blue-600',
      message: 'text-green-600',
      system: 'text-gray-600',
      challenge: 'text-purple-600'
    };
    return colors[type] || 'text-gray-600';
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.type === filter);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Bell className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Notification Center
            {unreadCount > 0 && (
              <Badge className="ml-3 bg-red-500 text-white">{unreadCount}</Badge>
            )}
          </h1>
          <p className="text-gray-600">Manage your notifications and alerts</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={markAllAsRead} disabled={unreadCount === 0}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark All Read
          </Button>
          <Button variant="outline" onClick={deleteAll} disabled={notifications.length === 0}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete All
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-[#0A1628]">{notifications.length}</p>
              </div>
              <Bell className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-[#0A1628]">
                  {notifications.filter(n => n.priority === 'high').length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Today</p>
                <p className="text-2xl font-bold text-[#0A1628]">3</p>
              </div>
              <Calendar className="h-8 w-8 text-[#D4AF37]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-2 mb-6">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          All
        </Button>
        <Button
          variant={filter === 'unread' ? 'default' : 'outline'}
          onClick={() => setFilter('unread')}
          className={filter === 'unread' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          Unread ({unreadCount})
        </Button>
        <Button
          variant={filter === 'referral' ? 'default' : 'outline'}
          onClick={() => setFilter('referral')}
          className={filter === 'referral' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          Referrals
        </Button>
        <Button
          variant={filter === 'appointment' ? 'default' : 'outline'}
          onClick={() => setFilter('appointment')}
          className={filter === 'appointment' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          Appointments
        </Button>
        <Button
          variant={filter === 'message' ? 'default' : 'outline'}
          onClick={() => setFilter('message')}
          className={filter === 'message' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          Messages
        </Button>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">No notifications</p>
            </CardContent>
          </Card>
        ) : (
          filteredNotifications.map(notification => (
            <Card 
              key={notification.id} 
              className={`hover:shadow-lg transition-shadow ${
                !notification.read ? 'border-l-4 border-[#D4AF37] bg-yellow-50' : ''
              }`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-full bg-white ${getTypeColor(notification.type)}`}>
                    {notification.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-[#0A1628]">{notification.title}</h3>
                          <Badge className={getPriorityColor(notification.priority)}>
                            {notification.priority}
                          </Badge>
                          {!notification.read && (
                            <Badge className="bg-[#D4AF37] text-[#0A1628]">New</Badge>
                          )}
                        </div>
                        <p className="text-gray-700 mb-2">{notification.message}</p>
                        <p className="text-sm text-gray-500">{notification.timestamp}</p>
                      </div>
                      <div className="flex space-x-2">
                        {!notification.read && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;